'use client'

import * as React from 'react'

import { cn } from '../lib/utils'

/**
 * FO-373 (batch-42) — LeverwijzeKiezer: het ene gedeelde keuzepatroon voor
 * "Uit voorraad" vs "Extern bestellen", per regel, over alle toeken-/bestel-
 * flows (uitrusting-beoordeling, gereedschap-uitgifte-set, directe toewijzing,
 * karren/koffers). Presentational + gecontroleerd: de caller houdt
 * `aantalVoorraad` (0..min(aantal, vrij)) en krijgt wijzigingen via `onChange`;
 * `aantalExtern` = aantal - aantalVoorraad.
 *
 * batch-45 (FO-381 §3.3) — optionele derde modus "Geen mutaties": alleen het
 * bezit registreren (het middel is al buiten het systeem om uitgereikt), zonder
 * voorraadmutatie of bestelling. Opt-in via `metGeenMutaties` + het
 * gecontroleerde `zonderMutatie`/`onZonderMutatieChange`-paar, zodat bestaande
 * callers ongewijzigd blijven. De registratie krijgt server-side de zichtbare,
 * niet-onderdrukbare markering "geregistreerd zonder voorraadmutatie".
 *
 * Bewust GEEN i18n-dependency in het package (net als RotatiePatroonEditor):
 * teksten met een zinnige NL-default, override via `labels`. STREEP-tokens
 * inline. Werkt op 375px (blokken stapelen).
 *
 * batch-57 (FO-419) — de 0-voorraad-tak. Sinds elk catalogus-artikel automatisch
 * een voorraad-artikel heeft, is "geen koppeling" geen normaal geval meer maar
 * een bewuste beheerkeuze. Staat er 0 op voorraad, dan zegt de kiezer dat ook
 * ("0 op voorraad - extern bestellen of ontvangst registreren") en biedt hij via
 * `onOntvangstRegistreren` een directe bijboek-actie, zodat een net binnen-
 * gekomen levering meteen uit voorraad geleverd kan worden.
 *
 * batch-62 (FO-467) — optionele derde leverwijze "Al geleverd - verwerken
 * zonder bestelling": de medewerker heeft het artikel al, er gaat geen
 * bestelling naar de leverancier en er wordt niets klaargelegd. Opt-in via
 * `metAlGeleverd` + het gecontroleerde `alGeleverd`/`onAlGeleverdChange`-paar.
 * De reden en het voorraadgevolg vraagt de caller zelf uit (die horen bij de
 * flow, niet bij de kiezer). Met de keuze aan liggen voorraad/extern stil
 * (lichter en uitgeschakeld), in alle drie de weergaven.
 *
 * batch-69 bestelflow (FO-512 §B.3) — optioneel "extern alleen voor wat er niet
 * ligt": met `externAlleenZonderVoorraad` kan het voorraaddeel niet onder de vrije
 * voorraad (begrensd op het aantal) zakken, staat "Alles extern" / de extern-knop
 * dicht zolang er iets vrij ligt, en toont de kiezer waarom, met de uitweg
 * `onVoorraadCorrigeren` ("Voorraad corrigeren naar 0, telling klopt niet") of,
 * zonder die actie, `voorraadCorrigerenHint` (bv. "vraag het magazijn"). De server
 * dwingt dezelfde regel af; dit is de zichtbare kant ervan.
 *
 * Gedrag:
 *  - Geen voorraad-koppeling (`heeftVoorraadKoppeling === false`): alleen extern,
 *    met de koppel-hint. `aantalVoorraad` staat vast op 0. (Mét `metGeenMutaties`
 *    blijft de geen-mutaties-keuze wél beschikbaar.)
 *  - Wel koppeling maar 0 vrij: default extern, maar de gebruiker kan bewust op
 *    voorraad blijven wachten -> hier tonen we het als "0 beschikbaar" en houden
 *    aantalVoorraad op 0 (splitsen kan niet zonder voorraad). Mét
 *    `toonNulVoorraadHint` komt daar de 0-op-voorraad-regel (+ ontvangst-actie)
 *    bij; die is opt-in omdat de tekst over extern bestellen en leveringen gaat
 *    en dus niet in elke flow klopt.
 *  - aantal === 1: een segmented toggle (2- of 3-knops).
 *  - aantal > 1 met vrije voorraad: de 2+1-splitsing met een aantal-stepper; de
 *    extern-rest volgt automatisch. Snelknoppen "Alles uit voorraad"/"Alles extern".
 *    De geen-mutaties-modus is daar regel-breed (checkbox: alles registreren).
 */

export interface LeverwijzeKiezerLabels {
  /** "Uit voorraad" (zonder aantal). */
  voorraad: string
  /** "Extern bestellen". */
  extern: string
  /** Suffix bij voorraad, bv. "{n} beschikbaar". Krijgt het vrije aantal. */
  beschikbaar: (vrij: number) => string
  /** Suffix bij voorraad met locatie, bv. "{n} beschikbaar op {locatie}". */
  beschikbaarOpLocatie: (vrij: number, locatie: string) => string
  /** Label boven de voorraad-stepper bij een bulk-splitsing. */
  uitVoorraadAantal: string
  /** Label voor het extern-deel bij een bulk-splitsing. */
  externAantal: string
  /** Snelknop "alles uit voorraad". */
  allesVoorraad: string
  /** Snelknop "alles extern". */
  allesExtern: string
  /** Hint als het artikel geen voorraad-koppeling heeft. */
  geenKoppelingHint: string
  /** batch-57 (FO-419) — regel bij een gekoppeld artikel met 0 vrije voorraad. */
  nulVoorraad: string
  /** batch-57 (FO-419) — knoptekst van de directe bijboek-actie. */
  ontvangstRegistreren: string
  /** batch-45 (FO-381) — derde modus: alleen registreren, geen voorraadmutatie. */
  geenMutaties: string
  /** Uitleg onder de geen-mutaties-keuze (markering is niet onderdrukbaar). */
  geenMutatiesUitleg: string
  /** batch-62 (FO-467) — derde leverwijze: al bij de medewerker, geen bestelling. */
  alGeleverd: string
  /** Uitleg onder de al-geleverd-keuze. */
  alGeleverdUitleg: string
  /** batch-69 (FO-512) — waarom extern (nog) niet kan; krijgt het vrije aantal. */
  externGeblokkeerd: (vrij: number) => string
  /** batch-69 (FO-512) — knoptekst van de correctie naar 0. */
  voorraadCorrigeren: string
}

export const LEVERWIJZE_KIEZER_DEFAULT_LABELS: LeverwijzeKiezerLabels = {
  voorraad: 'Uit voorraad',
  extern: 'Extern bestellen',
  beschikbaar: (vrij) => `${vrij} beschikbaar`,
  beschikbaarOpLocatie: (vrij, locatie) => `${vrij} beschikbaar op ${locatie}`,
  uitVoorraadAantal: 'Uit voorraad',
  externAantal: 'Extern bestellen',
  allesVoorraad: 'Alles uit voorraad',
  allesExtern: 'Alles extern',
  geenKoppelingHint:
    'Koppel het artikel aan de voorraad om uit voorraad te kunnen leveren.',
  nulVoorraad: '0 op voorraad - extern bestellen of ontvangst registreren',
  ontvangstRegistreren: 'Ontvangst registreren',
  geenMutaties: 'Geen mutaties',
  geenMutatiesUitleg:
    'Alleen registreren dat de medewerker dit heeft (al buiten het systeem om uitgereikt). Geen voorraadmutatie; de registratie krijgt de zichtbare markering "geregistreerd zonder voorraadmutatie".',
  alGeleverd: 'Al geleverd - verwerken zonder bestelling',
  alGeleverdUitleg:
    'De medewerker heeft het artikel al. Er gaat geen bestelling naar de leverancier en er wordt niets klaargelegd.',
  externGeblokkeerd: (vrij) =>
    `Er ligt nog ${vrij} vrij op voorraad: extern bestellen kan alleen voor wat er niet ligt.`,
  voorraadCorrigeren: 'Voorraad corrigeren naar 0 (telling klopt niet)',
}

export interface LeverwijzeKiezerProps {
  /** Totaal benodigd aantal op deze regel. */
  aantal: number
  /** Vrije voorraad op de gekoppelde bron (0 als er geen vrije voorraad is). */
  vrij: number
  /** Heeft het artikel een voorraad-koppeling? false -> alleen extern (+ hint). */
  heeftVoorraadKoppeling: boolean
  /** Gecontroleerde waarde: hoeveel uit voorraad (0..min(aantal, vrij)). */
  aantalVoorraad: number
  /** Wijziging van het voorraad-aantal (rest = extern). */
  onChange: (aantalVoorraad: number) => void
  /** Locatie-aanduiding voor "N beschikbaar op {locatie}" (optioneel). */
  locatieNaam?: string | null
  /** Leverancier-naam voor "Extern bestellen ({leverancier})" (optioneel). */
  leverancierNaam?: string | null
  disabled?: boolean
  labels?: Partial<LeverwijzeKiezerLabels>
  className?: string
  /** id-prefix voor labels (a11y); default random-vrij via React.useId. */
  idPrefix?: string
  /**
   * batch-45 (FO-381) — bied de derde modus "Geen mutaties" aan. Opt-in:
   * zonder deze vlag is het component exact het FO-373-gedrag.
   */
  metGeenMutaties?: boolean
  /** Gecontroleerd: de hele regel als "geen mutaties" registreren. */
  zonderMutatie?: boolean
  /** Wijziging van de geen-mutaties-keuze (regel-breed). */
  onZonderMutatieChange?: (zonderMutatie: boolean) => void
  /**
   * batch-57 (FO-419) — toon bij een GEKOPPELD artikel zonder vrije voorraad de
   * regel "0 op voorraad - extern bestellen of ontvangst registreren". Opt-in:
   * de tekst gaat over extern bestellen én over binnenkomende leveringen, en
   * slaat dus nergens op in flows die geen van beide kennen (onboarding-assets,
   * gereedschap-toewijzing). Zonder deze vlag is het component exact het
   * FO-373/FO-381-gedrag.
   */
  toonNulVoorraadHint?: boolean
  /**
   * batch-57 (FO-419) — directe bijboek-actie bij 0 op voorraad. Meegeven =
   * de knop "Ontvangst registreren" verschijnt onder de 0-op-voorraad-regel
   * (vereist `toonNulVoorraadHint`). Weglaten (bv. geen magazijnrechten) =
   * alleen de tekst.
   */
  onOntvangstRegistreren?: () => void
  /**
   * batch-62 (FO-467) — bied de derde leverwijze "Al geleverd - verwerken
   * zonder bestelling" aan. Opt-in: zonder deze vlag is het component exact het
   * bestaande gedrag.
   */
  metAlGeleverd?: boolean
  /** Gecontroleerd: de regel is al bij de medewerker (geen bestelling, geen picklijst). */
  alGeleverd?: boolean
  /** Wijziging van de al-geleverd-keuze. */
  onAlGeleverdChange?: (aan: boolean) => void
  /**
   * batch-69 bestelflow (FO-512 §B.3) — extern alleen voor het deel dat niet op
   * voorraad ligt. Opt-in: zonder deze vlag is het component exact het bestaande
   * gedrag (andere flows kennen deze regel niet).
   */
  externAlleenZonderVoorraad?: boolean
  /** De uitweg bij de blokkade: de voorraad corrigeren naar 0 (opent de caller-dialoog). */
  onVoorraadCorrigeren?: () => void
  /** Zonder correctie-actie (geen magazijnrechten): wat de gebruiker wél kan. */
  voorraadCorrigerenHint?: string
}

/** Klem een waarde op [0, max]. */
function clamp(n: number, max: number): number {
  if (!Number.isFinite(n)) return 0
  return Math.max(0, Math.min(Math.floor(n), max))
}

export function LeverwijzeKiezer({
  aantal,
  vrij,
  heeftVoorraadKoppeling,
  aantalVoorraad,
  onChange,
  locatieNaam,
  leverancierNaam,
  disabled = false,
  labels: labelOverrides,
  className,
  idPrefix,
  metGeenMutaties = false,
  zonderMutatie = false,
  onZonderMutatieChange,
  toonNulVoorraadHint = false,
  onOntvangstRegistreren,
  metAlGeleverd = false,
  alGeleverd = false,
  onAlGeleverdChange,
  externAlleenZonderVoorraad = false,
  onVoorraadCorrigeren,
  voorraadCorrigerenHint,
}: LeverwijzeKiezerProps) {
  const labels = { ...LEVERWIJZE_KIEZER_DEFAULT_LABELS, ...labelOverrides }
  const reactId = React.useId()
  const baseId = idPrefix ?? reactId

  const maxVoorraad = Math.min(aantal, Math.max(0, vrij))
  const kanVoorraad = heeftVoorraadKoppeling && maxVoorraad > 0
  const veiligVoorraad = clamp(aantalVoorraad, maxVoorraad)
  const aantalExtern = aantal - veiligVoorraad
  const geenMutatiesActief = metGeenMutaties && zonderMutatie
  // batch-69 (FO-512 §B.3) — met de opt-in mag het voorraaddeel niet onder wat er
  // vrij ligt: extern is dan alleen het deel dat er niet is.
  const minVoorraad = externAlleenZonderVoorraad && kanVoorraad ? maxVoorraad : 0
  const externGeblokkeerd = minVoorraad > 0

  // batch-62 (FO-467) — met "al geleverd" aan liggen voorraad/extern stil: de
  // kern-besturing gaat dicht (en wordt lichter weergegeven). Zonder de opt-in
  // is `kernDisabled` gewoon `disabled` en draagt de kern zelf de className,
  // precies zoals voorheen.
  const alGeleverdActief = metAlGeleverd && alGeleverd
  const kernDisabled = disabled || alGeleverdActief
  const kernClassName = metAlGeleverd ? undefined : className

  const voorraadTekst =
    locatieNaam && locatieNaam.trim().length > 0
      ? labels.beschikbaarOpLocatie(vrij, locatieNaam.trim())
      : labels.beschikbaar(vrij)
  const externTekst = leverancierNaam
    ? `${labels.extern} (${leverancierNaam})`
    : labels.extern

  // batch-45 (FO-381) — regel-brede geen-mutaties-checkbox (bulk-variant en de
  // geen-koppeling-variant delen 'm; de segmented toggle rendert een knop).
  const geenMutatiesCheckbox = metGeenMutaties ? (
    <label className="flex items-start gap-2 text-xs text-[#1A1A18]">
      <input
        type="checkbox"
        checked={zonderMutatie}
        disabled={kernDisabled}
        onChange={(e) => onZonderMutatieChange?.(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-[#CED0C8]"
      />
      <span>
        <span className="font-medium">{labels.geenMutaties}</span>
        <span className="mt-0.5 block text-[#5C5C58]">{labels.geenMutatiesUitleg}</span>
      </span>
    </label>
  ) : null

  // batch-57 (FO-419) — 0-op-voorraad-regel + directe bijboek-actie. Verschijnt
  // alleen bij een GEKOPPELD artikel zonder vrije voorraad: dan is "extern" geen
  // structureel gat maar een tijdelijke stand die met één handeling te
  // verhelpen is. Zonder `onOntvangstRegistreren` (geen magazijnrechten) blijft
  // alleen de tekst staan.
  const nulVoorraadBlok =
    toonNulVoorraadHint && heeftVoorraadKoppeling && vrij <= 0 && !geenMutatiesActief ? (
      <div className="flex flex-wrap items-center gap-2 text-xs text-[#5C5C58]">
        <span>{labels.nulVoorraad}</span>
        {onOntvangstRegistreren ? (
          <button
            type="button"
            disabled={kernDisabled}
            onClick={onOntvangstRegistreren}
            className="min-h-11 rounded-md border border-[#CED0C8] bg-white px-2.5 py-1 text-xs font-medium text-[#1A1A18] hover:bg-[#F0F1EC] disabled:opacity-40 md:min-h-0"
          >
            {labels.ontvangstRegistreren}
          </button>
        ) : null}
      </div>
    ) : null

  // batch-69 (FO-512 §B.3) — waarom extern dicht is, met de uitweg. Nooit een
  // blokkade zonder weg eruit: de correctie-knop, of de hint van de caller.
  const externBlokkadeBlok =
    externGeblokkeerd && !geenMutatiesActief ? (
      <div className="flex flex-wrap items-center gap-2 text-xs text-[#5C5C58]">
        <span>{labels.externGeblokkeerd(vrij)}</span>
        {onVoorraadCorrigeren ? (
          <button
            type="button"
            disabled={kernDisabled}
            onClick={onVoorraadCorrigeren}
            className="min-h-11 rounded-md border border-[#CED0C8] bg-white px-2.5 py-1 text-xs font-medium text-[#1A1A18] hover:bg-[#F0F1EC] disabled:opacity-40 md:min-h-0"
          >
            {labels.voorraadCorrigeren}
          </button>
        ) : voorraadCorrigerenHint ? (
          <span>{voorraadCorrigerenHint}</span>
        ) : null}
      </div>
    ) : null

  const renderKern = (): React.ReactElement => {
    // ── Geen koppeling: uitsluitend extern, met de koppel-hint. ──────────────
    if (!heeftVoorraadKoppeling && !metGeenMutaties) {
      return (
        <div className={cn('space-y-1', kernClassName)}>
          <div className="inline-flex items-center gap-1.5 rounded-md border border-[#CED0C8] bg-[#F0F1EC] px-2.5 py-1.5 text-xs text-[#1A1A18]">
            <span aria-hidden>📦</span>
            <span>{externTekst}</span>
          </div>
          <p className="text-xs text-[#5C5C58]">{labels.geenKoppelingHint}</p>
        </div>
      )
    }
    if (!heeftVoorraadKoppeling && metGeenMutaties) {
      // Zonder koppeling blijft extern de leverroute, maar de geen-mutaties-
      // registratie is wél beschikbaar (het middel is er mogelijk al).
      return (
        <div className={cn('space-y-2', kernClassName)}>
          {!geenMutatiesActief ? (
            <div className="space-y-1">
              <div className="inline-flex items-center gap-1.5 rounded-md border border-[#CED0C8] bg-[#F0F1EC] px-2.5 py-1.5 text-xs text-[#1A1A18]">
                <span aria-hidden>📦</span>
                <span>{externTekst}</span>
              </div>
              <p className="text-xs text-[#5C5C58]">{labels.geenKoppelingHint}</p>
            </div>
          ) : null}
          {geenMutatiesCheckbox}
        </div>
      )
    }

    // ── aantal === 1: segmented toggle (2- of 3-knops). ──────────────────────
    if (aantal === 1) {
      const toggle = (
        <div
          className={cn(
            'inline-flex overflow-hidden rounded-md border border-[#CED0C8]',
            nulVoorraadBlok || externBlokkadeBlok ? undefined : kernClassName,
          )}
          role="group"
          aria-label={
            metGeenMutaties
              ? `${labels.voorraad} / ${labels.extern} / ${labels.geenMutaties}`
              : `${labels.voorraad} / ${labels.extern}`
          }
        >
          <button
            type="button"
            disabled={kernDisabled || !kanVoorraad}
            aria-pressed={!geenMutatiesActief && veiligVoorraad === 1}
            onClick={() => {
              onZonderMutatieChange?.(false)
              onChange(1)
            }}
            className={cn(
              'min-h-11 px-3 py-1.5 text-xs font-medium transition-colors md:min-h-0',
              !geenMutatiesActief && veiligVoorraad === 1
                ? 'bg-[#CCFA78] text-[#1A1A18]'
                : 'bg-white text-[#5C5C58] hover:text-[#1A1A18] disabled:opacity-50',
            )}
          >
            {labels.voorraad}
            <span className="ml-1 text-[10px] font-normal opacity-70">({voorraadTekst})</span>
          </button>
          <button
            type="button"
            disabled={kernDisabled || externGeblokkeerd}
            title={externGeblokkeerd ? labels.externGeblokkeerd(vrij) : undefined}
            aria-pressed={!geenMutatiesActief && veiligVoorraad === 0}
            onClick={() => {
              onZonderMutatieChange?.(false)
              onChange(0)
            }}
            className={cn(
              'min-h-11 border-l border-[#CED0C8] px-3 py-1.5 text-xs font-medium transition-colors md:min-h-0',
              !geenMutatiesActief && veiligVoorraad === 0
                ? 'bg-[#1A1A18] text-white'
                : 'bg-white text-[#5C5C58] hover:text-[#1A1A18]',
            )}
          >
            {externTekst}
          </button>
          {metGeenMutaties ? (
            <button
              type="button"
              disabled={kernDisabled}
              aria-pressed={geenMutatiesActief}
              title={labels.geenMutatiesUitleg}
              onClick={() => {
                onChange(0)
                onZonderMutatieChange?.(true)
              }}
              className={cn(
                'min-h-11 border-l border-[#CED0C8] px-3 py-1.5 text-xs font-medium transition-colors md:min-h-0',
                geenMutatiesActief
                  ? 'bg-[#F0F1EC] text-[#1A1A18] underline decoration-dotted underline-offset-2'
                  : 'bg-white text-[#5C5C58] hover:text-[#1A1A18]',
              )}
            >
              {labels.geenMutaties}
            </button>
          ) : null}
        </div>
      )
      if (!nulVoorraadBlok && !externBlokkadeBlok) return toggle
      return (
        <div className={cn('space-y-2', kernClassName)}>
          {toggle}
          {nulVoorraadBlok}
          {externBlokkadeBlok}
        </div>
      )
    }

    // ── aantal > 1: de 2+1-splitsing. ────────────────────────────────────────
    // batch-45 (FO-381) — met de regel-brede geen-mutaties-keuze aan vervalt de
    // splitsing (alles wordt alleen geregistreerd).
    const stepperId = `${baseId}-voorraad-aantal`
    if (geenMutatiesActief) {
      return <div className={cn('space-y-2', kernClassName)}>{geenMutatiesCheckbox}</div>
    }
    return (
      <div className={cn('space-y-2', kernClassName)}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-2">
            <label htmlFor={stepperId} className="text-xs font-medium text-[#1A1A18]">
              {labels.uitVoorraadAantal}
            </label>
            <div className="inline-flex items-center rounded-md border border-[#CED0C8]">
              <button
                type="button"
                disabled={kernDisabled || veiligVoorraad <= minVoorraad}
                aria-label="minder uit voorraad"
                onClick={() => onChange(Math.max(minVoorraad, clamp(veiligVoorraad - 1, maxVoorraad)))}
                className="min-h-11 px-2.5 text-sm text-[#5C5C58] hover:text-[#1A1A18] disabled:opacity-40 md:min-h-0"
              >
                -
              </button>
              <input
                id={stepperId}
                type="number"
                inputMode="numeric"
                min={minVoorraad}
                max={maxVoorraad}
                disabled={kernDisabled || !kanVoorraad}
                value={veiligVoorraad}
                onChange={(e) => onChange(Math.max(minVoorraad, clamp(Number(e.target.value), maxVoorraad)))}
                className="w-12 border-x border-[#CED0C8] bg-white py-1.5 text-center text-sm tabular-nums text-[#1A1A18] disabled:bg-[#F0F1EC]"
              />
              <button
                type="button"
                disabled={kernDisabled || veiligVoorraad >= maxVoorraad}
                aria-label="meer uit voorraad"
                onClick={() => onChange(clamp(veiligVoorraad + 1, maxVoorraad))}
                className="min-h-11 px-2.5 text-sm text-[#5C5C58] hover:text-[#1A1A18] disabled:opacity-40 md:min-h-0"
              >
                +
              </button>
            </div>
            <span className="text-xs text-[#5C5C58]">({voorraadTekst})</span>
          </div>

          <div className="text-xs text-[#5C5C58]">
            <span className="font-medium text-[#1A1A18]">{labels.externAantal}:</span>{' '}
            <span className="tabular-nums">{aantalExtern}</span>
            {leverancierNaam ? ` (${leverancierNaam})` : ''}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={kernDisabled || !kanVoorraad || veiligVoorraad >= maxVoorraad}
            onClick={() => onChange(maxVoorraad)}
            className="rounded-md border border-[#CED0C8] bg-white px-2.5 py-1 text-xs text-[#5C5C58] hover:text-[#1A1A18] disabled:opacity-40"
          >
            {labels.allesVoorraad}
          </button>
          <button
            type="button"
            disabled={kernDisabled || veiligVoorraad === 0 || externGeblokkeerd}
            title={externGeblokkeerd ? labels.externGeblokkeerd(vrij) : undefined}
            onClick={() => onChange(0)}
            className="rounded-md border border-[#CED0C8] bg-white px-2.5 py-1 text-xs text-[#5C5C58] hover:text-[#1A1A18] disabled:opacity-40"
          >
            {labels.allesExtern}
          </button>
        </div>

        {nulVoorraadBlok}
        {externBlokkadeBlok}
        {geenMutatiesCheckbox}
      </div>
    )
  }

  const kern = renderKern()
  if (!metAlGeleverd) return kern

  // batch-62 (FO-467) — de derde leverwijze als eigen, altijd bereikbare knop
  // onder de voorraad/extern-keuze (ook bij "geen koppeling" en bij 0 vrij: juist
  // dan komt het voor dat het artikel al buiten het systeem om is uitgereikt).
  return (
    <div className={cn('space-y-2', className)}>
      <div
        className={cn(alGeleverdActief && 'opacity-50')}
        aria-disabled={alGeleverdActief || undefined}
      >
        {kern}
      </div>
      <div className="space-y-1">
        <button
          type="button"
          disabled={disabled}
          aria-pressed={alGeleverdActief}
          onClick={() => onAlGeleverdChange?.(!alGeleverdActief)}
          className={cn(
            'min-h-11 rounded-md border px-3 py-1.5 text-left text-xs font-medium transition-colors md:min-h-0',
            alGeleverdActief
              ? 'border-[#1A1A18] bg-[#CCFA78] text-[#1A1A18]'
              : 'border-[#CED0C8] bg-white text-[#5C5C58] hover:text-[#1A1A18] disabled:opacity-50',
          )}
        >
          {labels.alGeleverd}
        </button>
        {alGeleverdActief ? (
          <p className="text-xs text-[#5C5C58]">{labels.alGeleverdUitleg}</p>
        ) : null}
      </div>
    </div>
  )
}
